import { api, unwrap, type ApiEnvelope } from "@/lib/api";

export type AutomaticDiscountType = "PERCENT" | "FIXED";
export type CustomerScope = "ALL" | "FIRST_ORDER" | "B2B" | "B2C" | "SPECIFIC";
export type DynamicAutoStatus = "active" | "inactive" | "expired" | "scheduled";

export interface AutomaticDiscount {
	id: string;
	name: string;
	description: string | null;
	type: AutomaticDiscountType;
	value: number;
	priority: number;
	isActive: boolean;
	startsAt: string | null;
	expiresAt: string | null;
	minSubtotal: number;
	maxDiscount: number;
	productIds: string[];
	categoryIds: string[];
	customerScope: CustomerScope;
	customerIds: string[];
	allowCumulationWithCoupons: boolean;
	allowCumulationWithOtherAuto: boolean;
	redeemedCount: number;
	totalDiscountDistributed: number;
	createdAt: string;
	updatedAt: string;
}

export interface AutomaticDiscountKpis {
	activeCount: number;
	scheduledCount: number;
	expiredCount: number;
	inactiveCount: number;
	totalCount: number;
	totalDiscountDistributed: number;
	totalRedemptions: number;
}

export interface AutomaticDiscountsFilter {
	search?: string;
	status?: DynamicAutoStatus;
	page?: number;
	limit?: number;
}

export interface PaginatedAutomaticDiscounts {
	items: AutomaticDiscount[];
	total: number;
	page: number;
	limit: number;
	pages: number;
}

export interface CreateAutomaticDiscountInput {
	name: string;
	description?: string | null;
	type: AutomaticDiscountType;
	value: number;
	priority?: number;
	isActive?: boolean;
	startsAt?: string | null;
	expiresAt?: string | null;
	minSubtotal?: number;
	maxDiscount?: number;
	productIds?: string[] | null;
	categoryIds?: string[] | null;
	customerScope?: CustomerScope;
	customerIds?: string[] | null;
	allowCumulationWithCoupons?: boolean;
	allowCumulationWithOtherAuto?: boolean;
}

export type UpdateAutomaticDiscountInput = Partial<CreateAutomaticDiscountInput>;

const toQs = (filter: AutomaticDiscountsFilter): string => {
	const params = new URLSearchParams();
	if (filter.search) params.set("search", filter.search);
	if (filter.status) params.set("status", filter.status);
	if (filter.page) params.set("page", String(filter.page));
	if (filter.limit) params.set("limit", String(filter.limit));
	const str = params.toString();
	return str ? `?${str}` : "";
};

export const automaticDiscountsApi = {
	list: (filter: AutomaticDiscountsFilter = {}) =>
		unwrap<PaginatedAutomaticDiscounts>(
			api.get<ApiEnvelope<PaginatedAutomaticDiscounts>>(
				`/admin/promotions/automatic${toQs(filter)}`,
			),
		),

	getKpis: () =>
		unwrap<AutomaticDiscountKpis>(
			api.get<ApiEnvelope<AutomaticDiscountKpis>>(
				"/admin/promotions/automatic/kpi",
			),
		),

	get: (id: string) =>
		unwrap<AutomaticDiscount>(
			api.get<ApiEnvelope<AutomaticDiscount>>(
				`/admin/promotions/automatic/${id}`,
			),
		),

	create: (data: CreateAutomaticDiscountInput) =>
		unwrap<AutomaticDiscount>(
			api.post<ApiEnvelope<AutomaticDiscount>>(
				"/admin/promotions/automatic",
				data,
			),
		),

	update: (id: string, data: UpdateAutomaticDiscountInput) =>
		unwrap<AutomaticDiscount>(
			api.patch<ApiEnvelope<AutomaticDiscount>>(
				`/admin/promotions/automatic/${id}`,
				data,
			),
		),

	toggleStatus: (id: string, isActive: boolean) =>
		unwrap<AutomaticDiscount>(
			api.patch<ApiEnvelope<AutomaticDiscount>>(
				`/admin/promotions/automatic/${id}/status`,
				{ isActive },
			),
		),
};
